import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};

export const AppProvider = ({ children }) => {
  const { user } = useAuth();

  // ── Core data ──────────────────────────────────────────────────────────────
  const [subjects, setSubjects] = useState([]);
  const [topicStatuses, setTopicStatuses] = useState({}); // key: subjectId_topicKey → status
  const [recentlyGreenedCount, setRecentlyGreenedCount] = useState(0);
  const [exams, setExams] = useState([]);
  const [scores, setScores] = useState([]);
  const [notes, setNotes] = useState([]);
  const [studyPlan, setStudyPlan] = useState(null); // latest generated plan
  const [admissionsProfile, setAdmissionsProfile] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);

  // ── Derived state (computed, not stored) ──────────────────────────────────
  const upcomingExams = exams
    .filter(e => new Date(e.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const nextExam = upcomingExams[0] || null;

  const daysUntil = (date) => Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));

  // Turns a stored topicKey into a human label. Handles:
  //  - "topic_Name_With_Underscores" (top-level topic; Lesson.js/DiagnosticTest.js/Subject.js)
  //  - "topic_Parent_Name__sub_Subtopic_Name" (subtopic; Subject.js)
  //  - legacy numeric/positional keys ("8_2", "3") from before this convention existed
  // The hard rule: never return a string that's just digits/underscores — if a key can't
  // be resolved to a real name, it's dropped rather than shown as e.g. "8 2".
  const labelForTopicKey = (rawKey) => {
    let key = rawKey.replace(/^topic_/, '');
    const subIdx = key.indexOf('__sub_');
    if (subIdx !== -1) {
      const subName = key.substring(subIdx + 6).replace(/_/g, ' ').trim();
      return /^[\d\s]*$/.test(subName) ? null : subName;
    }
    const label = key.replace(/_/g, ' ').trim();
    return /^[\d\s]*$/.test(label) ? null : label;
  };

  const weakTopics = Object.entries(topicStatuses)
    .filter(([, v]) => v === 'red')
    .map(([key]) => {
      // Key format: subjectId_topicKey
      const underscoreIdx = key.indexOf('_');
      if (underscoreIdx === -1) return null;
      const subjectId = key.substring(0, underscoreIdx);
      const label = labelForTopicKey(key.substring(underscoreIdx + 1));
      if (!label) return null;
      const subject = subjects.find(s => s.id === subjectId);
      return { key, subjectId, topicKey: label, subjectName: subject?.name, subjectColor: subject?.color };
    })
    .filter(Boolean);

  const strongTopics = Object.entries(topicStatuses)
    .filter(([, v]) => v === 'green').length;

  const amberTopics = Object.entries(topicStatuses)
    .filter(([, v]) => v === 'amber').length;

  const totalRated = Object.keys(topicStatuses).length;

  const overallMastery = totalRated > 0
    ? Math.round(((strongTopics + amberTopics * 0.5) / totalRated) * 100)
    : 0;

  const getSubjectProgress = useCallback((subjectId) => {
    const entries = Object.entries(topicStatuses).filter(([k]) => k.startsWith(subjectId + '_'));
    if (!entries.length) return { pct: 0, green: 0, amber: 0, red: 0, total: 0 };
    const green = entries.filter(([, v]) => v === 'green').length;
    const amber = entries.filter(([, v]) => v === 'amber').length;
    const red = entries.filter(([, v]) => v === 'red').length;
    return { pct: Math.round(((green + amber * 0.5) / entries.length) * 100), green, amber, red, total: entries.length };
  }, [topicStatuses]);

  const getSubjectById = useCallback((id) => subjects.find(s => s.id === id), [subjects]);

  // ── Today's context (what the AI Tutor and planner use) ───────────────────
  const todayContext = {
    nextExam: nextExam ? {
      subject: nextExam.subject,
      date: nextExam.date,
      daysAway: daysUntil(nextExam.date),
    } : null,
    weakTopics: weakTopics.slice(0, 5),
    upcomingExams: upcomingExams.slice(0, 5).map(e => ({
      subject: e.subject,
      date: e.date,
      daysAway: daysUntil(e.date),
    })),
    overallMastery,
    subjectCount: subjects.length,
    recentScores: scores.slice(0, 3),
  };

  const buildAIContext = useCallback((subjectId = null) => {
    const subject = subjectId ? subjects.find(s => s.id === subjectId) : null;
    const subjectProg = subjectId ? getSubjectProgress(subjectId) : null;
    const subjectWeak = subjectId
      ? Object.entries(topicStatuses)
          .filter(([k, v]) => k.startsWith(subjectId + '_') && v === 'red')
          .map(([k]) => labelForTopicKey(k.substring(subjectId.length + 1)))
          .filter(Boolean)
          .slice(0, 8)
      : [];
    const subjectStrong = subjectId
      ? Object.entries(topicStatuses)
          .filter(([k, v]) => k.startsWith(subjectId + '_') && v === 'green')
          .map(([k]) => labelForTopicKey(k.substring(subjectId.length + 1)))
          .filter(Boolean)
          .slice(0, 5)
      : [];

    let ctx = `═══ STUDENT INTELLIGENCE PROFILE ═══\n\n`;

    // Subject-specific context
    if (subject) {
      ctx += `SUBJECT: ${subject.name}${subject.qualification ? ` (${subject.qualification})` : ''}\n`;
      if (subjectProg?.total > 0) {
        ctx += `Mastery: ${subjectProg.pct}% (${subjectProg.green} confident, ${subjectProg.amber} developing, ${subjectProg.red} gaps)\n`;
      }
      if (subjectWeak.length > 0) ctx += `Gaps needing work: ${subjectWeak.join(', ')}\n`;
      if (subjectStrong.length > 0) ctx += `Already mastered: ${subjectStrong.join(', ')}\n`;
    }

    // Exams
    if (upcomingExams.length > 0) {
      ctx += `\nEXAMS:\n`;
      upcomingExams.slice(0, 5).forEach(e => {
        ctx += `  • ${e.subject}: ${daysUntil(e.date)} days away (${new Date(e.date).toLocaleDateString('en-GB')})\n`;
      });
    }

    // Overall mastery
    ctx += `\nOVERALL MASTERY: ${overallMastery}%\n`;
    if (weakTopics.length > 0) {
      ctx += `Biggest gaps across all subjects: ${weakTopics.slice(0, 5).map(t => `${t.topicKey} (${t.subjectName})`).join(', ')}\n`;
    }

    // Recent scores
    if (scores.length > 0) {
      ctx += `\nRECENT TEST SCORES:\n`;
      scores.slice(0, 5).forEach(s => {
        ctx += `  • ${s.subject || 'Unknown'}: ${s.score}${s.maxScore ? `/${s.maxScore}` : ''}${s.notes ? ` — "${s.notes}"` : ''} (${new Date(s.date).toLocaleDateString('en-GB')})\n`;
      });
    }

    // Saved notes — this is the memory layer
    if (notes.length > 0) {
      const subjectNotes = subjectId ? notes.filter(n => n.subjectId === subjectId) : notes;
      const recentNotes = subjectNotes.slice(0, 5);
      if (recentNotes.length > 0) {
        ctx += `\nSAVED NOTES & UPLOADED MATERIAL:\n`;
        recentNotes.forEach(n => {
          const preview = typeof n.content === 'string' ? n.content.slice(0, 400) : '';
          ctx += `  • "${n.title}" (${new Date(n.updatedAt || n.createdAt).toLocaleDateString('en-GB')}):\n    ${preview}${preview.length === 400 ? '...' : ''}\n`;
        });
      }
    }

    // Study plan
    if (studyPlan?.plan) {
      ctx += `\nACTIVE STUDY PLAN:\n${typeof studyPlan.plan === 'string' ? studyPlan.plan.slice(0, 500) : JSON.stringify(studyPlan.plan).slice(0, 500)}\n`;
    }

    // Student profile
    if (userProfile) {
      ctx += `\n## Student Profile\n`;
      if (userProfile.displayName) ctx += `Name: ${userProfile.displayName}\n`;
      if (userProfile.school) ctx += `School: ${userProfile.school}\n`;
      if (userProfile.gradYear) ctx += `Graduation year: ${userProfile.gradYear}\n`;
      if (userProfile.learningStyle) ctx += `Learning style: ${userProfile.learningStyle}\n`;
      if (userProfile.studyHoursPerDay) ctx += `Study hours per day: ${userProfile.studyHoursPerDay}\n`;
      if (userProfile.preferredStudyTime) ctx += `Preferred study time: ${userProfile.preferredStudyTime}\n`;
      if (userProfile.targetUniversities?.length) ctx += `Target universities: ${userProfile.targetUniversities.filter(Boolean).join(', ')}\n`;
      if (userProfile.motivation) ctx += `Motivation: ${userProfile.motivation}\n`;
      if (userProfile.weakAreasNote) ctx += `Areas of struggle: ${userProfile.weakAreasNote}\n`;
      if (userProfile.strengthsNote) ctx += `Strengths: ${userProfile.strengthsNote}\n`;
      if (userProfile.examFear) ctx += `Exam worry: ${userProfile.examFear}\n`;
    }

    ctx += `\n═══ END PROFILE ═══\n`;
    ctx += `\nIMPORTANT: Use this profile naturally. Reference specific weak topics, note content, and scores when relevant. If the student asks about something from their notes or a previous upload, answer from that content. Never pretend you don't know things that are in this profile.`;

    return ctx;
  }, [subjects, topicStatuses, upcomingExams, nextExam, overallMastery, weakTopics, scores, notes, studyPlan, userProfile, getSubjectProgress]);

  // ── Fetch all data once ───────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const [subSnap, statusSnap, examSnap, scoreSnap, noteSnap, planSnap, admSnap, profileSnap] = await Promise.all([
        getDocs(query(collection(db, 'subjects'), where('userId', '==', user.uid))),
        getDocs(query(collection(db, 'topicStatuses'), where('userId', '==', user.uid))),
        getDocs(query(collection(db, 'exams'), where('userId', '==', user.uid))),
        getDocs(query(collection(db, 'scores'), where('userId', '==', user.uid))),
        getDocs(query(collection(db, 'notes'), where('userId', '==', user.uid))),
        getDocs(query(collection(db, 'studyPlans'), where('userId', '==', user.uid))),
        getDoc(doc(db, 'admissionsProfiles', user.uid)).catch(() => null),
        getDoc(doc(db, 'userProfile', user.uid)).catch(() => null),
      ]);

      setSubjects(subSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const st = {};
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      let greenedThisMonth = 0;
      statusSnap.docs.forEach(d => {
        const data = d.data();
        const stKey = data.subjectId + '_' + data.topicKey;
        st[stKey] = data.status;
        if (data.status === 'green' && data.updatedAt && data.updatedAt >= thirtyDaysAgo) greenedThisMonth++;
      });
      setTopicStatuses(st);
      setRecentlyGreenedCount(greenedThisMonth);

      setExams(examSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setScores(scoreSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.date) - new Date(a.date)));
      setNotes(noteSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const plans = planSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (plans.length > 0) setStudyPlan(plans[0]);

      if (admSnap?.exists?.()) setAdmissionsProfile(admSnap.data());
      if (profileSnap?.exists?.()) setUserProfile(profileSnap.data());

      setLastFetched(new Date());
    } catch (err) {  }
    setLoading(false);
  }, [user?.uid]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Helpers for updating state without re-fetching ─────────────────────────
  const updateTopicStatus = (subjectId, topicKey, status) => {
    setTopicStatuses(prev => ({ ...prev, [subjectId + '_' + topicKey]: status }));
  };

  const addSubjectToState = (subject) => setSubjects(prev => [...prev, subject]);
  const removeSubjectFromState = (id) => setSubjects(prev => prev.filter(s => s.id !== id));
  const addExamToState = (exam) => setExams(prev => [...prev, exam]);
  const addScoreToState = (score) => setScores(prev => [score, ...prev]);
  const addNoteToState = (note) => setNotes(prev => [note, ...prev]);
  const updateAdmissionsProfile = (profile) => setAdmissionsProfile(profile);

  return (
    <AppContext.Provider value={{
      // Raw data
      subjects, topicStatuses, recentlyGreenedCount, exams, scores, notes, studyPlan, admissionsProfile, userProfile, loading, lastFetched,
      // Derived
      upcomingExams, nextExam, weakTopics, strongTopics, totalRated, overallMastery, todayContext,
      // Helpers
      getSubjectProgress, getSubjectById, buildAIContext, daysUntil,
      // Updaters
      updateTopicStatus, addSubjectToState, removeSubjectFromState,
      addExamToState, addScoreToState, addNoteToState, updateAdmissionsProfile,
      // Refetch
      refresh: fetchAll,
    }}>
      {children}
    </AppContext.Provider>
  );
};
